import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ShowCaptionEvent, ShowStatus } from '@fetmode/types';
import {
  LiveVideoControls,
  StreamHealthBanner,
  useLivePlayback,
  type StreamHealthSnapshot,
} from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

const CAPTIONS_STORAGE_KEY = 'fetmode.liveCaptions';

function readCaptionsPref(): boolean {
  try {
    return window.localStorage.getItem(CAPTIONS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export interface ShowLivePlayerProps {
  status: ShowStatus;
  remoteStream: MediaStream | null;
  connecting: boolean;
  placeholderSrc: string;
  modelName: string;
  notifyArmed?: boolean;
  onNotifyMe?: () => void;
  onKeepBrowsing?: () => void;
  health?: StreamHealthSnapshot;
  paid?: boolean;
  localPreviewStream?: MediaStream | null;
  onVideoElement?: (video: HTMLVideoElement | null) => void;
  fullscreenTarget?: HTMLElement | null;
  caption?: ShowCaptionEvent | null;
}

export function ShowLivePlayer({
  status,
  remoteStream,
  connecting,
  placeholderSrc,
  modelName,
  notifyArmed = false,
  onNotifyMe,
  onKeepBrowsing,
  health,
  paid = false,
  localPreviewStream = null,
  onVideoElement,
  fullscreenTarget = null,
  caption = null,
}: ShowLivePlayerProps): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pipRef = useRef<HTMLVideoElement | null>(null);
  const wasLiveRef = useRef(false);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const { muted, volume, setMuted, setVolume } = useLivePlayback(videoEl, true);
  const [captionsOn, setCaptionsOn] = useState(readCaptionsPref);
  const live = remoteStream !== null;
  const waiting = connecting && !live;

  useEffect(() => {
    if (live && !wasLiveRef.current) {
      setMuted(false);
    } else if (!live) {
      setMuted(true);
    }
    wasLiveRef.current = live;
  }, [live, setMuted]);

  useEffect(() => {
    const video = videoRef.current;
    setVideoEl(video);
    if (!video) {
      return;
    }
    if (remoteStream) {
      video.srcObject = remoteStream;
      video.removeAttribute('src');
      void video.play().catch(() => undefined);
      return;
    }
    video.srcObject = null;
    if (waiting) {
      video.removeAttribute('src');
      return;
    }
    if (video.getAttribute('src') !== placeholderSrc) {
      video.src = placeholderSrc;
      void video.play().catch(() => undefined);
    }
  }, [remoteStream, placeholderSrc, live, waiting]);

  useEffect(() => {
    onVideoElement?.(videoRef.current);
    return () => {
      onVideoElement?.(null);
    };
  }, [onVideoElement]);

  useEffect(() => {
    const video = pipRef.current;
    if (!video) {
      return;
    }
    if (localPreviewStream) {
      video.srcObject = localPreviewStream;
      void video.play().catch(() => undefined);
      return;
    }
    video.srcObject = null;
  }, [localPreviewStream]);

  useEffect(() => {
    try {
      window.localStorage.setItem(CAPTIONS_STORAGE_KEY, captionsOn ? '1' : '0');
    } catch {
      // Ignore quota / private mode.
    }
  }, [captionsOn]);

  return (
    <>
      <video
        ref={videoRef}
        className={
          status === 'PAUSED' || status === 'AWAY'
            ? 'show-room__player-video show-room__player-video--break'
            : 'show-room__player-video'
        }
        hidden={waiting}
        autoPlay
        muted={muted}
        loop={!live}
        playsInline
        preload="auto"
        aria-label={live ? t('common.show.playerLive') : t('common.show.playerPlaceholder')}
      />
      {waiting ? (
        <div className="show-room__player-wait" role="status">
          <div className="show-room__player-wait-card">
            <p className="show-room__player-wait-kicker">{modelName}</p>
            <h2 className="show-room__player-wait-title">{t('common.show.playerWaitingTitle')}</h2>
            <p className="show-room__player-wait-body">{t('common.show.playerWaitingBody')}</p>
            <div className="show-room__player-wait-actions">
              {onNotifyMe ? (
                <Button
                  label={
                    notifyArmed
                      ? t('common.show.playerWaitingNotifyOn')
                      : t('common.show.playerWaitingNotify')
                  }
                  variant={notifyArmed ? 'secondary' : 'primary'}
                  onClick={onNotifyMe}
                  disabled={notifyArmed}
                />
              ) : null}
              {onKeepBrowsing ? (
                <Button
                  label={t('common.show.playerWaitingBrowse')}
                  variant="ghost"
                  onClick={onKeepBrowsing}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {live && health ? (
        <div className="show-room__player-health">
          <StreamHealthBanner health={health} paid={paid} side="fan" />
        </div>
      ) : null}
      {live && captionsOn && caption ? (
        <p
          className={`fm-live-captions${caption.isFinal ? '' : ' fm-live-captions--interim'}`}
          aria-live="polite"
        >
          {caption.text}
        </p>
      ) : null}
      <LiveVideoControls
        muted={muted}
        volume={volume}
        onMutedChange={setMuted}
        onVolumeChange={setVolume}
        fullscreenTarget={fullscreenTarget}
        captionsEnabled={captionsOn}
        onCaptionsToggle={
          live
            ? () => {
                setCaptionsOn((current) => !current);
              }
            : undefined
        }
      />
      {localPreviewStream ? (
        <video
          ref={pipRef}
          className="show-room__c2c-pip"
          autoPlay
          muted
          playsInline
          aria-label={t('common.show.cam2camStart')}
        />
      ) : null}
    </>
  );
}
